import React from 'react';
import LegalPage from '@/components/LegalPage';
import usePageTitle from '@/hooks/usePageTitle';

const EntrantConsent = () => {
  usePageTitle('Entrant Consent');
  return (
  <LegalPage
    kicker="Legal"
    title="Entrant Consent &amp; Declaration"
    lastUpdated="02 July 2026"
  >
    <p>
      This is the consent and declaration that every nominator confirms when
      submitting an entry to <strong>The AdAura Awards 2026</strong>. The
      declaration is presented as a mandatory checkbox on the final step of
      the entry form; ticking it constitutes acceptance of the statements
      below in full.
    </p>

    <h2>1. Accuracy of information</h2>
    <p>
      I confirm that all information supplied in this entry — including the
      campaign title, category, credits (brand, agency, production house,
      freelancers, jurors), objectives, execution details, results, dates,
      spend and any supporting metrics — is <strong>true, accurate and complete</strong> to the
      best of my knowledge. I understand that misleading or false claims are
      grounds for immediate disqualification without refund, and, where
      published, for public retraction.
    </p>

    <h2>2. Authority to submit</h2>
    <p>
      I confirm that I am authorised by the brand, agency, or other party on
      whose behalf this entry is submitted, and that I have obtained all
      necessary internal approvals to enter the work. Where a brand approval
      letter is required by the entry form, I have uploaded a valid one.
    </p>

    <h2>3. Intellectual property &amp; clearances</h2>
    <p>
      I confirm that the submitting party owns, or has obtained the rights to
      use, every element of the entry material — including music, footage,
      talent, model releases, voice-over artistes, brand/product IP and any
      celebrity association — and that publication of the entry on the AdAura
      platform, jury chamber, award-night reels, social media and the AdAura
      editorial / case-study library <em>as described in our{' '}
      <a href="/ip-policy">Copyright &amp; IP Policy</a></em>{' '}
      does not infringe any third-party right.
    </p>
    <p>
      Where any specific asset (a music cue, a talent appearance, a client
      logo) is licensed only for a restricted use, I have flagged the
      restriction in the entry form so Marketing Mind can restrict use
      accordingly.
    </p>

    <h2>4. Licence granted for judging and publication</h2>
    <p>
      In exchange for participation, I grant Marketing Mind the{' '}
      <strong>non-exclusive, royalty-free, worldwide licence</strong> described
      in §2 of the <a href="/ip-policy">IP Policy</a> to use the submitted
      material for judging, ceremony, editorial coverage, social promotion of
      shortlisted / winning work, and the winners’ archive. Ownership of the
      work remains fully with the original rights-holders.
    </p>

    <h2>5. Data &amp; contact</h2>
    <p>
      I consent to Marketing Mind and its processors (Razorpay, Google
      Workspace, Cloudflare R2, MongoDB Atlas, Google reCAPTCHA) processing my
      personal data for the entry, payment and lifecycle communication as
      described in the <a href="/privacy">Privacy Policy</a>. I understand I
      can exercise my access, correction and erasure rights under the DPDP Act
      2023 by writing to <a href="mailto:awards@marketingmind.co">awards@marketingmind.co</a>.
    </p>

    <h2>6. Jury independence</h2>
    <p>
      I understand that the jury is <strong>independent</strong> and that its
      decision is <strong>final</strong>, that entries are judged blind (with
      credits stripped), that Marketing Mind does not disclose per-juror
      scores, and that not winning is not grounds for refund or appeal.
    </p>

    <h2>7. Payment &amp; refunds</h2>
    <p>
      I agree to the <a href="/payment-terms">Payment Terms</a> and{' '}
      <a href="/refund">Refund &amp; Cancellation Policy</a>. I understand that
      the entry is considered submitted only when Razorpay confirms a captured
      payment.
    </p>

    <h2>8. Fair use of the platform</h2>
    <p>
      I confirm I will not attempt to bypass the entry form, upload malicious
      files, misrepresent the identity of the submitting party, or use any
      automated tool to interact with the site. I understand such attempts are
      logged and may result in the entry being disqualified and my account
      being suspended.
    </p>

    <h2>9. Indemnity</h2>
    <p>
      I agree to indemnify and hold harmless Marketing Mind, Digi Giant LLP,
      their officers and jurors from any third-party claim arising out of a
      breach of the statements above.
    </p>

    <p>
      By ticking <em>“I confirm that all information submitted is accurate and
      complete, and that all necessary permissions and approvals have been
      obtained…”</em> at the end of the entry form, I acknowledge that I have
      read and accepted this Consent &amp; Declaration together with the AdAura
      Awards <a href="/terms">Terms &amp; Conditions</a>,{' '}
      <a href="/privacy">Privacy Policy</a>,{' '}
      <a href="/payment-terms">Payment Terms</a> and{' '}
      <a href="/ip-policy">IP Policy</a>.
    </p>
  </LegalPage>
  );
};

export default EntrantConsent;
